import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api-client';
import { API_BASE } from './api-base';
import { queryKeys } from './query-client';
import { useMe } from './me';
import { calendarOwnerKey } from './calendar/calendar-storage';
import { customerHistoryReadFrom, type CustomerHistoryRead } from './customer-history';

async function fetchCustomerHistory(emails: string[], phones: string[]): Promise<CustomerHistoryRead> {
  let res: Response;
  try {
    // A POST so the person's addresses ride in the body, never in a logged URL. The body is lookup keys only.
    res = await apiFetch(`${API_BASE}/api/customer-history`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails, phones }),
    });
  } catch {
    return { status: 'failed' };
  }
  const body = res.status === 200 ? await res.json().catch(() => null) : null;
  return customerHistoryReadFrom(res.status, body);
}

/**
 * The contact's customer history across the owner's Wix sites (`native-apps/shared/customer-history/contract.md`),
 * keyed on the sorted lookup keys (`customerLookupKeys`). Off when the contact has no email or phone. A failed
 * read is an answer (`failed`), never a thrown error, so the screen draws it rather than retrying.
 */
export function useCustomerHistory(keys: { emails: string[]; phones: string[] }) {
  const ownerKey = calendarOwnerKey(useMe());
  const { emails, phones } = keys;
  return useQuery({
    queryKey: queryKeys.customerHistory(ownerKey, emails, phones),
    queryFn: () => fetchCustomerHistory(emails, phones),
    enabled: !!ownerKey && (emails.length > 0 || phones.length > 0),
    staleTime: 60_000,
  });
}
